import { FormattedMessage } from "react-intl";

import { CounterInputField } from "@ps-design-system/components/CounterInput";
import { InputField } from "@ps-design-system/components/Input";
import { Text } from "@ps-design-system/components/Text";

import { useIntl } from "react-intl-compat";
import { getCurrencySymbol } from "~/i18n/i18n.utils";

import type { ListingUseType } from "../../../../../../components/src/services/listings.service";
import {
  MAX_HOURLY_RATE,
  MAX_MINIMUM_DURATION_HOURS,
  USE_TYPE_SLUG,
} from "../../ActivateListingsPage.consts";

import { CleaningFeeFields } from "./CleaningFeeFields";
import { DayRateDiscountField } from "./DayRateDiscountField";

type PricingFieldsProps = {
  spaceCountry: string;
  useType: ListingUseType;
};

/** The "Pricing" section: hourly rate, minimum duration, day rate discount, and cleaning fee fields. */
const PricingFields = ({ spaceCountry, useType }: PricingFieldsProps) => {
  const intl = useIntl();
  const currencySymbol = getCurrencySymbol(spaceCountry);
  const slug = USE_TYPE_SLUG[useType];

  return (
    <>
      <Text
        typeface="headline-2-m"
        element="p"
        className="tw-border-b tw-border-gray-300 tw-py-8 tw-mb-8"
        testId={`activate-listing-form-${slug}-pricing-header`}
      >
        <FormattedMessage
          id="hosting.activate_listings.pricing_header"
          defaultMessage="Pricing"
        />
      </Text>

      <InputField
        fullWidth
        type="number"
        name="hourlyRate"
        prefix={currencySymbol}
        label={intl.formatMessage({
          id: "hosting.activate_listings.hourly_rate_label",
          defaultMessage: "Hourly rate",
        })}
        note={intl.formatMessage({
          id: "hosting.activate_listings.hourly_rate_note",
          defaultMessage:
            "You can customize your pricing with attendee pricing, calendar pricing, and add-ons once your listings are created.",
        })}
        min={0}
        max={MAX_HOURLY_RATE}
        placeholder="75"
        required
        testId={`activate-listing-form-${slug}-hourly-rate-input`}
      />

      {/** @ts-ignore - issues in design system */}
      <CounterInputField
        fullWidth={true}
        required={true}
        name="minimumDuration"
        min={0}
        max={MAX_MINIMUM_DURATION_HOURS}
        note={
          <FormattedMessage
            id="hosting.activate_listings.minimum_duration_note"
            defaultMessage="Must be between 1-12 hours"
          />
        }
        label={
          <FormattedMessage
            id="hosting.activate_listings.minimum_duration_label"
            defaultMessage="Minimum number of hours"
          />
        }
        placeholder="4"
        testId={`activate-listing-form-${slug}-minimum-duration-input`}
      />

      <DayRateDiscountField spaceCountry={spaceCountry} useType={useType} />

      <CleaningFeeFields spaceCountry={spaceCountry} useType={useType} />
    </>
  );
};

export { PricingFields };
